import React, { useEffect, useState } from "react";
import { X, Info, UserCheck, Search } from "lucide-react";
import useSWR from "swr";
import { useMemo } from "react";
import type { CourtRoomRef } from "./types";

interface AssignCourtroomModalProps {
  isOpen: boolean;
  officer: any;
  highCourts: any[];
  courtRooms: CourtRoomRef[];
  districts: any[];
  onClose: () => void;
  onSave: (payload: {
    officerId: string;
    assignmentId: string;
    stateName?: string;
    districtId?: string;
    highCourtId?: string;
    courtrooms?: string[];
  }) => Promise<void>;
}

const AssignCourtroomModal: React.FC<AssignCourtroomModalProps> = ({
  isOpen,
  officer,
  highCourts,
  // courtRooms,
  districts,
  onClose,
  onSave,
}) => {
  const [highCourtId, setHighCourtId] = useState("");
  const [assignmentId, setAssignmentId] = useState("");
  const [stateName, setStateName] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [complexId, setComplexId] = useState("");
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState<{
    highCourtId?: string;
    assignmentId?: string;
    courtrooms?: string;
    districtId?: string;
    complexId?: string;
  }>({});
  const [courtrooms, setCourtrooms] = useState<{ id: string; name: string }[]>(
    [],
  );
  const { data: benchData } = useSWR(
    highCourtId
      ? `/api/court/get-benches/${highCourtId}?page=1&limit=100`
      : null,
  );

  const { data: benchRoomData } = useSWR(
    officer?.courtType === "high_courts" && assignmentId
      ? `/api/court/get-bench-rooms/${assignmentId}?page=1&limit=100`
      : null,
  );

  const benchRooms = benchRoomData?.data || [];

  const { data: complexData } = useSWR(
    districtId
      ? `/api/court/get-complexes/${districtId}?page=1&limit=100`
      : null,
  );
  const { data: districtCourtRoomData } = useSWR(
    officer?.courtType === "district_courts" && complexId
      ? `/api/court/get-courtrooms/${complexId}?page=1&limit=100`
      : null,
  );
  const filteredDistricts = useMemo(
    () =>
      districts.filter(
        (district) => !stateName || district.stateName === stateName,
      ),
    [districts, stateName],
  );

  const complexes = complexData?.data || [];
  const benches = benchData?.data || [];
  const districtCourtRooms = districtCourtRoomData?.data || [];

  const filteredDistrictCourtrooms = districtCourtRooms.filter((room: any) =>
    room.courtRoomName?.toLowerCase().includes(search.toLowerCase()),
  );

  const filteredBenchRooms = benchRooms.filter((room: any) =>
    `${room.benchName} ${room.courtRoom}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  useEffect(() => {
    setHighCourtId("");
    setAssignmentId("");
    setStateName("");
    setDistrictId("");
    setComplexId("");
    setCourtrooms([]);
    setSearch("");
    setErrors({});
  }, [officer]);

  if (!isOpen || !officer) return null;

  const initials = `${officer.firstName?.[0] || ""}${
    officer.lastName?.[0] || ""
  }`;
  const role = officer.role;

  const roleConfig =
    role === "Controller"
      ? {
          color: "text-purple-700",
          border: "border-purple-600",
          badge: "bg-purple-100 text-purple-700",
          iconColor: "text-purple-700",
          button: "bg-purple-600",
          selectBorder: "border-purple-200 focus:border-purple-500",
          title: "Assign Courtrooms",
          description:
            "Scope to a complex first, then select up to 10 courtrooms.",
        }
      : role === "Judge"
        ? {
            color: "text-blue-700",
            border: "border-blue-600",
            badge: "bg-blue-100 text-blue-700",
            iconColor: "text-blue-700",
            button: "bg-blue-600",
            selectBorder: "border-blue-200 focus:border-blue-500",
            title: "Assign Courtroom",
            description: "Assign this Judge to a High Court bench.",
          }
        : {
            color: "text-green-700",
            border: "border-green-600",
            badge: "bg-green-100 text-green-700",
            iconColor: "text-green-700",
            button: "bg-green-600",
            selectBorder: "border-green-200 focus:border-green-500",
            title: "Assign to Courtroom",
            description:
              "This Reader will assist any judge presiding in the selected courtroom.",
          };

  const isSaveDisabled =
    officer?.courtType === "high_courts"
      ? !highCourtId ||
        !assignmentId ||
        ((role === "Reader" || role === "Controller") &&
          courtrooms.length === 0)
      : officer?.courtType === "district_courts"
        ? !districtId ||
          !complexId ||
          ((role === "Reader" || role === "Controller") &&
            courtrooms.length === 0)
        : true;
  const handleSave = async () => {
    const nextErrors: {
      highCourtId?: string;
      assignmentId?: string;
      courtrooms?: string;
      districtId?: string;
      complexId?: string;
    } = {};

    if (officer?.courtType === "high_courts") {
      if (!highCourtId) {
        nextErrors.highCourtId = "Please select a High Court.";
      } else if (!assignmentId) {
        nextErrors.assignmentId = "Please select a Bench.";
      } else if (
        (role === "Reader" || role === "Controller") &&
        courtrooms.length === 0
      ) {
        nextErrors.courtrooms = "Please select a Courtroom.";
      }
    }

    if (officer?.courtType === "district_courts") {
      if (!districtId) {
        nextErrors.districtId = "Please select a District Court.";
      } else if (!complexId) {
        nextErrors.complexId = "Please select a Complex / Bench.";
      } else if (
        (role === "Reader" || role === "Controller") &&
        courtrooms.length === 0
      ) {
        nextErrors.courtrooms = "Please select a Courtroom.";
      }
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    await onSave({
      officerId: officer._id,
      assignmentId,
      stateName,
      districtId,
      highCourtId,
      courtrooms: courtrooms.map((room) => room.id),
    });
  };
  return (
    <div className="fixed inset-0 z-[9999] bg-black/20">
      <div className="fixed right-0 top-0 flex h-screen w-full max-w-[480px] flex-col overflow-hidden bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              Assign Courtroom
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Reassigning for {officer.firstName} {officer.lastName}
            </p>
          </div>

          <button
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-4">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full border-2 font-semibold ${roleConfig.border} ${roleConfig.color}`}
              >
                {initials}
              </div>

              <div>
                <div className="text-lg font-semibold">
                  {officer.firstName} {officer.lastName}
                </div>

                <span
                  className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${roleConfig.badge}`}
                >
                  {officer.role}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-5 flex gap-4">
            <div
              className={`w-1 rounded ${
                role === "Controller"
                  ? "bg-purple-600"
                  : role === "Judge"
                    ? "bg-blue-600"
                    : "bg-green-600"
              }`}
            />

            <div>
              <div
                className={`flex items-center gap-2 text-base font-semibold ${roleConfig.color}`}
              >
                <UserCheck size={20} />
                {roleConfig.title}
              </div>

              <p className="mt-1 text-sm text-slate-600">
                {roleConfig.description}
              </p>
            </div>
          </div>
          <div className="mb-5 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-500">
            <Info size={16} />
            {officer?.courtType === "high_courts"
              ? "Scoped to High Courts"
              : "Scoped to District Courts"}
          </div>

          <div>
            {officer?.courtType === "high_courts" ? (
              <>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  High Court
                </label>

                <select
                  value={highCourtId}
                  onChange={(e) => {
                    setHighCourtId(e.target.value);
                    setAssignmentId("");
                    setCourtrooms([]);
                    setSearch("");
                    setErrors((current) => ({
                      ...current,
                      highCourtId: undefined,
                      assignmentId: undefined,
                      courtrooms: undefined,
                    }));
                  }}
                  className={`h-12 w-full rounded-lg border ${
                    errors.highCourtId
                      ? "border-red-400 focus:border-red-500"
                      : roleConfig.selectBorder
                  } bg-white px-4 outline-none`}
                >
                  <option value="" disabled>— Select High Court —</option>

                  {[...highCourts]
                    .sort((a, b) =>
                      String(a.highCourtName || "").localeCompare(
                        String(b.highCourtName || ""),
                      ),
                    )
                    .map((court) => (
                      <option key={court._id} value={court._id}>
                        {court.highCourtName}
                      </option>
                    ))}
                </select>
                {errors.highCourtId && (
                  <p className="mt-1 text-xs font-medium text-red-600">
                    {errors.highCourtId}
                  </p>
                )}
                {highCourtId && (
                  <>
                    <div className="mt-4">
                      <label className="mb-2 block text-sm font-semibold text-slate-700">
                        Bench
                      </label>

                      <select
                        value={assignmentId}
                        onChange={(e) => {
                          setAssignmentId(e.target.value);
                          setCourtrooms([]);
                          setSearch("");

                          setErrors((current) => ({
                            ...current,
                            assignmentId: undefined,
                            courtrooms: undefined,
                          }));
                        }}
                        className={`h-12 w-full rounded-lg border ${roleConfig.selectBorder} bg-white px-4 outline-none`}
                      >
                        <option value="" disabled>
                          Select Bench
                        </option>

                        {benches.map((bench: any) => (
                          <option key={bench._id} value={bench._id}>
                            {bench.benchName}
                          </option>
                        ))}
                      </select>
                      {errors.assignmentId && (
                        <p className="mt-1 text-xs font-medium text-red-600">
                          {errors.assignmentId}
                        </p>
                      )}
                    </div>

                    {assignmentId &&
                      (role === "Reader" || role === "Controller") && (
                        <div className="mt-5 flex items-center justify-between">
                          <label className="text-sm font-semibold">
                            Courtrooms
                          </label>

                          <span
                            className={`rounded-full px-2 py-1 text-xs font-bold ${
                              role === "Reader"
                                ? "bg-green-100 text-green-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {courtrooms.length} / {role === "Reader" ? 1 : 10}
                          </span>
                        </div>
                      )}

                    {(role === "Reader" || role === "Controller") &&
                      courtrooms.map((room) => (
                        <span
                          key={room.id}
                          className="inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-700"
                        >
                          {room.name}

                          <button
                            type="button"
                            onClick={() =>
                              setCourtrooms(
                                courtrooms.filter((r) => r.id !== room.id),
                              )
                            }
                          >
                            <X size={14} />
                          </button>
                        </span>
                      ))}

                    {assignmentId &&
                      (role === "Reader" || role === "Controller") && (
                        <div className="relative mt-3">
                          <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />

                          <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search courtrooms..."
                            className={`h-11 w-full rounded-lg border pl-9 pr-3 ${
                              role === "Reader"
                                ? "border-green-200"
                                : "border-purple-200"
                            }`}
                          />
                        </div>
                      )}

                    {assignmentId &&
                      (role === "Reader" || role === "Controller") && (
                        <>
                          <div
                            className={`mt-3 max-h-48 overflow-y-auto rounded-lg border ${
                              role === "Reader"
                                ? "border-green-200"
                                : "border-purple-200"
                            }`}
                          >
                            {filteredBenchRooms.map((room: any) => (
                              <label
                                key={room._id}
                                className="flex cursor-pointer items-center gap-3 border-b border-[#D1D5DB] px-4 py-3 last:border-b-0"
                              >
                                <input
                                  type="checkbox"
                                  checked={courtrooms.some(
                                    (r) => r.id === room._id,
                                  )}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (role === "Reader") {
                                        // Only one courtroom allowed.
                                        // Selecting a new one automatically replaces the old one.
                                        setCourtrooms([
                                          {
                                            id: room._id,
                                            name: `${room.benchName} (${room.courtRoom})`,
                                          },
                                        ]);
                                      } else {
                                        // Controller: maximum 10 courtrooms
                                        if (courtrooms.length >= 10) return;

                                        setCourtrooms([
                                          ...courtrooms,
                                          {
                                            id: room._id,
                                            name: `${room.benchName} (${room.courtRoom})`,
                                          },
                                        ]);
                                      }

                                      setErrors((current) => ({
                                        ...current,
                                        courtrooms: undefined,
                                      }));
                                    } else {
                                      setCourtrooms(
                                        courtrooms.filter(
                                          (r) => r.id !== room._id,
                                        ),
                                      );
                                    }
                                  }}
                                />

                                <span>
                                  {room.benchName} ({room.courtRoom})
                                </span>
                              </label>
                            ))}

                            {filteredBenchRooms.length === 0 && (
                              <div className="px-4 py-3 text-sm text-slate-500">
                                No courtrooms found.
                              </div>
                            )}
                          </div>
                          {errors.courtrooms && (
                            <p className="mt-1 text-xs font-medium text-red-600">
                              {errors.courtrooms}
                            </p>
                          )}
                        </>
                      )}
                  </>
                )}
              </>
            ) : (
              <>
                {/* <label className="mb-2 block text-sm font-semibold text-slate-700">
                  State
                </label>

                <select
                  value={stateName}
                  onChange={(e) => {
                    setStateName(e.target.value);
                    setDistrictId("");
                    setComplexId("");
                  }}
                  className={`h-12 w-full rounded-lg border ${roleConfig.selectBorder} bg-white px-4 outline-none`}
                >
                  <option value="">Select State</option>

                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select> */}

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    District Court
                  </label>

                  <select
                    value={districtId}
                    onChange={(e) => {
                      setDistrictId(e.target.value);
                      setComplexId("");
                      setAssignmentId("");
                      setCourtrooms([]);

                      setErrors((current) => ({
                        ...current,
                        districtId: undefined,
                        complexId: undefined,
                        courtrooms: undefined,
                      }));
                    }}
                    className={`h-12 w-full rounded-lg border ${roleConfig.selectBorder} bg-white px-4 outline-none`}
                  >
                    <option value="" disabled>
                      Select District
                    </option>

                    {[...filteredDistricts]
                      .sort((a: any, b: any) =>
                        String(a.districtName || "").localeCompare(
                          String(b.districtName || ""),
                        ),
                      )
                      .map((district: any) => (
                        <option key={district._id} value={district._id}>
                          {district.districtName}
                        </option>
                      ))}
                  </select>
                  {errors.districtId && (
                    <p className="mt-1 text-xs font-medium text-red-600">
                      {errors.districtId}
                    </p>
                  )}
                </div>

                {districtId && (
                  <div className="mt-4">
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Complex/Bench
                    </label>

                    <select
                      value={complexId}
                      onChange={(e) => {
                        setComplexId(e.target.value);
                        setAssignmentId(e.target.value);
                        setCourtrooms([]);

                        setErrors((current) => ({
                          ...current,
                          complexId: undefined,
                          courtrooms: undefined,
                        }));
                      }}
                      className={`h-12 w-full rounded-lg border ${roleConfig.selectBorder} bg-white px-4 outline-none`}
                    >
                      <option value="" disabled>
                        Select Complex
                      </option>

                      {[...complexes]
                        .sort((a: any, b: any) =>
                          String(a.complexName || "").localeCompare(
                            String(b.complexName || ""),
                          ),
                        )
                        .map((complex: any) => (
                          <option key={complex._id} value={complex._id}>
                            {complex.complexName}
                          </option>
                        ))}
                    </select>
                    {errors.complexId && (
                      <p className="mt-1 text-xs font-medium text-red-600">
                        {errors.complexId}
                      </p>
                    )}
                  </div>
                )}
                {complexId && (role === "Reader" || role === "Controller") && (
                  <>
                    <div className="mt-5 flex items-center justify-between">
                      <label className="text-sm font-semibold">
                        Assign Courtroom
                      </label>

                      <span
                        className={`rounded-full px-2 py-1 text-xs font-bold ${
                          role === "Reader"
                            ? "bg-green-100 text-green-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {courtrooms.length} / {role === "Reader" ? 1 : 10}
                      </span>
                    </div>

                    {courtrooms.length > 0 && (
                      <div className="mb-3 mt-3 flex flex-wrap gap-2">
                        {courtrooms.map((room) => (
                          <span
                            key={room.id}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${
                              role === "Reader"
                                ? "bg-green-100 text-green-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {room.name}

                            <button
                              type="button"
                              onClick={() =>
                                setCourtrooms(
                                  courtrooms.filter((r) => r.id !== room.id),
                                )
                              }
                            >
                              <X size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="relative mt-3">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />

                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search courtrooms..."
                        className={`h-11 w-full rounded-lg border pl-9 pr-3 ${
                          role === "Reader"
                            ? "border-green-200"
                            : "border-purple-200"
                        }`}
                      />
                    </div>

                    <div
                      className={`mt-3 max-h-48 overflow-y-auto rounded-lg border ${
                        role === "Reader"
                          ? "border-green-200"
                          : "border-purple-200"
                      }`}
                    >
                      {filteredDistrictCourtrooms.length > 0 ? (
                        filteredDistrictCourtrooms.map((room: any) => (
                          <label
                            key={room._id}
                            className="flex cursor-pointer items-center gap-3 border-b border-[#D1D5DB] px-4 py-3 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={courtrooms.some(
                                (r) => r.id === room._id,
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (!room._id || !room.courtRoomName) return;

                                  if (role === "Reader") {
                                    // Only one courtroom.
                                    // New selection replaces previous selection.
                                    setCourtrooms([
                                      {
                                        id: room._id,
                                        name: room.courtRoomName,
                                      },
                                    ]);
                                  } else {
                                    // Controller: maximum 10 courtrooms
                                    if (courtrooms.length >= 10) return;

                                    setCourtrooms([
                                      ...courtrooms,
                                      {
                                        id: room._id,
                                        name: room.courtRoomName,
                                      },
                                    ]);
                                  }

                                  setErrors((current) => ({
                                    ...current,
                                    courtrooms: undefined,
                                  }));
                                } else {
                                  setCourtrooms(
                                    courtrooms.filter((r) => r.id !== room._id),
                                  );
                                }
                              }}
                            />

                            <span>{room.courtRoomName}</span>
                          </label>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-slate-500">
                          No courtrooms found.
                        </div>
                      )}
                    </div>
                    {errors.courtrooms && (
                      <p className="mt-1 text-xs font-medium text-red-600">
                        {errors.courtrooms}
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex gap-4 border-t border-slate-200 bg-white p-6">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 py-3 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className={`flex-1 rounded-lg py-3 font-semibold text-white ${
              isSaveDisabled
                ? "cursor-not-allowed bg-slate-300"
                : roleConfig.button
            }`}
          >
            Save Assignment
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignCourtroomModal;
